import React, { useState, useEffect } from 'react';
import {
  Modal,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
} from 'reactstrap';
import { motion } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';
import { formatCreatedDate } from '../constants/utils';
import api from '../helpers/api';
import SparePartsModal from './SparePartsModal';

const SparePartTypeModal = ({ isOpen, toggle, sparePartTypeData, loading, onEditSparePartType = null }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('info');
  const [spareParts, setSpareParts] = useState([]);
  const [sparePartsModalState, setSparePartsModalState] = useState({ isOpen: false, sparePartData: null, loading: false });
  const [sparePartsLoading, setSparePartsLoading] = useState(false);

  // Fetch spare parts using this type
  const fetchSpareParts = async () => {
    if (!sparePartTypeData?.id) return;

    setSparePartsLoading(true);
    try {
      const { data } = await api.get(`organization-spare-part-type/get-all/spare-parts?sparePartTypeId=${sparePartTypeData.id}`);
      setSpareParts(data?.response || []);
    } catch (error) {
      console.error('Error fetching spare parts:', error);
      setSpareParts([]);
    } finally {
      setSparePartsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'spareParts' && sparePartTypeData?.id) {
      fetchSpareParts();
    }
  }, [activeTab, sparePartTypeData?.id]);

  // Reset tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('info');
    }
  }, [isOpen]);

  if (!sparePartTypeData && !loading) return null;

  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Header */}
        <div
          className="modal-header border-0 bg-gradient-primary text-white position-sticky"
          style={{
            top: 0,
            zIndex: 10,
            background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
            padding: '1.5rem'
          }}
        >
          <div className="d-flex align-items-center">
            <div className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center me-3"
                 style={{ width: '40px', height: '40px' }}>
              <i className="bx bx-cog text-white fs-5"></i>
            </div>
            <div>
              <h5 className="modal-title mb-0 fw-bold text-white">
                {sparePartTypeData?.name || t('business.components.spare_part_type_modal.title_default', 'Spare Part Type Details')}
              </h5>
              <p className="text-white-50 small mb-0">{t('business.components.spare_part_type_modal.subtitle', 'Spare part type information and details')}</p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3 ms-auto">
            {onEditSparePartType && sparePartTypeData && (
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                onClick={() => onEditSparePartType(sparePartTypeData)}
                title={t('shared.edit_spare_part_type', 'Edit Spare Part Type')}
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  color: '#495057',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  borderRadius: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.95)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                <FeatherIcon icon="edit-3" size={16} />
                <span>{t('shared.edit_spare_part_type', 'Edit Spare Part Type')}</span>
              </button>
            )}
            <button
              onClick={toggle}
              type="button"
              className="btn-close btn-close-white"
              aria-label={t('shared.close', 'Close')}
            />
          </div>
        </div>

        {/* Navigation Tabs */}
        <Nav
          tabs
          className="border-bottom-0 px-4 pt-3 bg-light"
          style={{ borderRadius: '0' }}
        >
          <NavItem>
            <NavLink
              className={`nav-link-modern ${activeTab === 'info' ? 'active' : ''}`}
              onClick={() => setActiveTab('info')}
              style={{
                border: 'none',
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === 'info' ? '#fff' : 'transparent',
                color: activeTab === 'info' ? '#495057' : '#6c757d',
                fontWeight: activeTab === 'info' ? '600' : '400',
                cursor: 'pointer'
              }}
            >
              <i className="bx bx-info-circle me-2"></i>
              {t('shared.info', 'Info')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={`nav-link-modern ${activeTab === 'spareParts' ? 'active' : ''}`}
              onClick={() => setActiveTab('spareParts')}
              style={{
                border: 'none',
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === 'spareParts' ? '#fff' : 'transparent',
                color: activeTab === 'spareParts' ? '#495057' : '#6c757d',
                fontWeight: activeTab === 'spareParts' ? '600' : '400',
                cursor: 'pointer'
              }}
            >
              <i className="bx bx-wrench me-2"></i>
              {t('shared.spare_parts', 'Spare Parts')}
            </NavLink>
          </NavItem>
        </Nav>

        {/* Modal Body */}
        <div
          className="modal-body p-0"
          style={{
            maxHeight: 'calc(90vh - 200px)',
            overflow: 'auto'
          }}
        >
          <TabContent activeTab={activeTab} className="h-100">
            {/* Info Tab */}
            <TabPane tabId="info">
              <div className="p-4">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <div className="info-icon me-3">
                        <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                             style={{ width: '40px', height: '40px' }}>
                          <i className="bx bx-cog text-primary fs-5"></i>
                        </div>
                      </div>
                      <div>
                        <h5 className="mb-1 fw-bold">{t('business.components.spare_part_type_modal.info_title', 'Spare Part Type Information')}</h5>
                        <p className="text-muted small mb-0">{t('business.components.spare_part_type_modal.info_subtitle', 'Complete spare part type details')}</p>
                      </div>
                    </div>

                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" size="lg" />
                        <p className="text-muted mt-3">{t('business.components.spare_part_type_modal.loading_details', 'Loading spare part type details...')}</p>
                      </div>
                    ) : (
                      sparePartTypeData && (
                        <Row className="g-4">
                          {/* Basic Information */}
                          <Col md={6}>
                            <div className="info-card bg-light rounded p-3 h-100">
                              <h6 className="text-primary mb-3 fw-semibold">
                                <i className="bx bx-info-circle me-2"></i>{t('shared.basic_information', 'Basic Information')}
                              </h6>
                              <div className="info-item mb-3">
                                <label className="text-muted small">{t('shared.name', 'Name')}</label>
                                <div className="fw-medium">{sparePartTypeData?.name || 'N/A'}</div>
                              </div>
                            </div>
                          </Col>

                          {/* Activity */}
                          <Col md={6}>
                            <div className="info-card bg-light rounded p-3 h-100">
                              <h6 className="text-primary mb-3 fw-semibold">
                                <i className="bx bx-time me-2"></i>{t('shared.activity', 'Activity')}
                              </h6>
                              {sparePartTypeData?.createdAt && (
                                <div className="info-item mb-3">
                                  <div className="fw-medium text-muted">
                                    {t('shared.created_by_on', 'Created {{user}} on {{date}}', {
                                      user: sparePartTypeData?.createdByUser?.name ? `${t('shared.by', 'By')} ${sparePartTypeData.createdByUser.name} ` : '',
                                      date: formatCreatedDate(sparePartTypeData?.createdAt)
                                    })}
                                  </div>
                                </div>
                              )}
                              {sparePartTypeData?.updatedAt && sparePartTypeData?.createdAt !== sparePartTypeData?.updatedAt && (
                                <div className="info-item">
                                  <div className="fw-medium text-muted">
                                    {t('shared.last_updated_by_on', 'Last updated {{user}} on {{date}}', {
                                      user: sparePartTypeData?.updatedByUser?.name ? `${t('shared.by', 'By')} ${sparePartTypeData.updatedByUser.name} ` : '',
                                      date: formatCreatedDate(sparePartTypeData?.updatedAt)
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </Col>
                        </Row>
                      )
                    )}
                  </CardBody>
                </Card>
              </div>
            </TabPane>

            {/* Spare Parts Tab */}
            <TabPane tabId="spareParts">
              <div className="p-4">
                {sparePartsLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="text-muted mt-3">{t('business.components.spare_part_type_modal.loading_spare_parts', 'Loading spare parts...')}</p>
                  </div>
                ) : spareParts.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>{t('shared.name', 'Name')}</th>
                          <th>{t('business.spare_parts.in_stock_quantity', 'In Stock')}</th>
                          <th>{t('shared.criticality', 'Criticality')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {spareParts.map((part, index) => (
                          <tr key={part.id}>
                            <th scope="row">{index + 1}</th>
                            <td
                              className="text-primary"
                              role="button"
                              style={{ cursor: 'pointer', textDecoration: 'underline' }}
                              onClick={() => {
                                setSparePartsModalState(prev => ({ ...prev, loading: true, isOpen: true }));
                                api.get(`spare-parts/get-by-id?id=${part.id}`).then(({ data }) => {
                                  setSparePartsModalState({ isOpen: true, sparePartData: data?.response, loading: false });
                                }).catch((err) => {
                                  console.error('Error fetching spare part details:', err);
                                  setSparePartsModalState({ isOpen: false, sparePartData: null, loading: false });
                                });
                              }}
                            >
                              {part.name || '-'}
                            </td>
                            <td>{part.quantity ?? '-'}</td>
                            <td>
                              {part.criticality ? (
                                <Badge color={part.criticality === 'HIGH' ? 'danger' : part.criticality === 'MEDIUM' ? 'warning' : 'success'}>
                                  {part.criticality}
                                </Badge>
                              ) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <div className="mb-3">
                      <i className="bx bx-wrench text-muted" style={{ fontSize: '3rem' }}></i>
                    </div>
                    <h5 className="text-muted mb-2">{t('business.components.spare_part_type_modal.no_spare_parts', 'No spare parts yet')}</h5>
                    <p className="text-muted mb-4">
                      {t('business.components.spare_part_type_modal.no_spare_parts_desc', "This spare part type hasn't been assigned to any spare parts yet.")}
                    </p>
                    <div className="alert alert-light border d-inline-block text-start">
                      <div className="d-flex align-items-start">
                        <i className="bx bx-bulb text-warning me-2 mt-1"></i>
                        <div>
                          <strong>{t('business.components.spare_part_type_modal.how_to_use', 'How to use this spare part type:')}</strong>
                          <ol className="mb-0 mt-2 ps-3">
                            <li>{t('business.components.spare_part_type_modal.how_to_use_step_1', 'Go to Spare Parts')}</li>
                            <li>{t('business.components.spare_part_type_modal.how_to_use_step_2', 'Create a new spare part or edit an existing one')}</li>
                            <li>{t('business.components.spare_part_type_modal.how_to_use_step_3', 'Select "{{name}}" from the Type dropdown', {
                              name: sparePartTypeData?.name || ''
                            })}</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </TabPane>
          </TabContent>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer border-0 pt-0">
          <button type="button" className="btn btn-secondary" onClick={toggle}>
            {t('shared.close', 'Close')}
          </button>
        </div>
      </motion.div>

      {/* Spare Part Detail Modal */}
      {sparePartsModalState.isOpen && (
        <SparePartsModal
          isOpen={sparePartsModalState.isOpen}
          toggle={() => setSparePartsModalState({ isOpen: false, sparePartData: null, loading: false })}
          sparePartData={sparePartsModalState.sparePartData}
          loading={sparePartsModalState.loading}
        />
      )}
    </Modal>
  );
};

export default SparePartTypeModal;
